import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router";
import { TooltipV2 } from "../tooltipV2.js";
import {
  glossaryOverlayOnLoad,
  glossaryOverlayClose,
  glossaryOverlayCtaClick,
} from "../../../screens/glossary/glossary.analytics.js";
import getGlossary from "../../../services/glossaryService";
import {
  AUTH_SESSION_STATUS,
  AuthSessionContext,
} from "../../contexts/authSessionContext";

const GlossaryTooltipContext = createContext(null);

/**
 * GlossaryTooltipProvider centralizes glossary-backed tooltip behavior across the app.
 * - Fetches glossary once on mount and indexes terms for O(1) lookup.
 * - Exposes helpers to get glossary tooltip data by term, open/close the shared popup, and list known terms.
 * - Renders the shared TooltipV2 UI when a glossary tooltip is active.
 *
 * Usage:
 *   <GlossaryTooltipProvider>
 *     <App />
 *   </GlossaryTooltipProvider>
 *
 * TODO: Add Analytics
 */
export const GlossaryTooltipProvider = ({ children }) => {
  /**
   * @typedef {Object} GlossaryTooltipPayload
   * @property {Object} tooltipData - Tooltip data object (term, category, description).
   * @property {string} [pageName] - Page name for analytics attribution.
   * @property {Function} [navigate] - Navigation function to route to glossary on "More" click.
   * @property {Function} [clickAnalytics] - Analytics emitter used by glossary overlay events.
   */

  /** @type {[GlossaryTooltipPayload|null, Function]} activeGlossaryTooltip - Currently displayed tooltip payload. */
  const [activeGlossaryTooltip, setActiveGlossaryTooltip] = useState(null);
  /** @type {[Object, Function]} glossaryMap - Map of term (lowercased) -> glossary item. */
  const [glossaryMap, setGlossaryMap] = useState({});
  /** @type {[boolean, Function]} isLoadingGlossary - Indicates glossary fetch state. */
  const [isLoadingGlossary, setIsLoadingGlossary] = useState(false);
  const { status: authStatus } = useContext(AuthSessionContext);
  const hasFetchedGlossaryRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Close tooltip when navigation changes
  useEffect(() => {
    if (activeGlossaryTooltip) {
      closeGlossaryTooltip();
    }
  }, [location.pathname, location.search]);

  // Fetch glossary once after authentication is confirmed so the API call
  // always carries a valid auth header. On first mobile login the OIDC user
  // isn't hydrated until AuthSessionProvider resolves, so fetching on mount
  // would fire without credentials and silently fail.
  useEffect(() => {
    if (authStatus !== AUTH_SESSION_STATUS.AUTHENTICATED) return;
    if (hasFetchedGlossaryRef.current) return;
    hasFetchedGlossaryRef.current = true;

    let isMounted = true;
    (async () => {
      try {
        setIsLoadingGlossary(true);
        const glossaryResponse = await getGlossary();
        if (!isMounted) return;

        const glossary = Array.isArray(glossaryResponse)
          ? glossaryResponse
          : Array.isArray(glossaryResponse?.Items)
            ? glossaryResponse.Items
            : null;

        if (!glossary) {
          console.error(
            "GlossaryTooltipProvider: glossary response was not an array",
            glossaryResponse,
          );
          return;
        }

        // TODO: fetched vs indexed terms are different numbers
        const map = glossary.reduce((acc, item) => {
          if (item?.term) {
            acc[item.term.toLowerCase()] = item;
          }
          return acc;
        }, {});

        if (glossary.length === 0) {
          console.error("GlossaryTooltipProvider: glossary is empty");
        }

        setGlossaryMap(map);
      } catch (e) {
        console.error("GlossaryTooltipProvider: failed to load glossary", e);
        // swallow; consumers can handle missing tooltip data
      } finally {
        isMounted && setIsLoadingGlossary(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [authStatus]);

  const getGlossaryTooltipData = (term) => {
    if (!term) return null;
    return glossaryMap[term.toLowerCase()] || null;
  };

  /** Close the active tooltip popup. */
  const closeGlossaryTooltip = () => setActiveGlossaryTooltip(null);

  /**
   * Open the shared tooltip popup with analytics + navigation context.
   * @param {TooltipPayload} param0
   */
  const openGlossaryTooltip = ({
    tooltipData,
    pageName,
    navigate: customNavigate,
    clickAnalytics,
    triggerRef,
  }) => {
    if (!tooltipData) return;
    setActiveGlossaryTooltip({
      tooltipData,
      pageName,
      navigate: customNavigate || navigate, // Use custom navigate if provided, otherwise use router navigate
      clickAnalytics,
      triggerRef,
    });
  };

  const glossaryTerms = useMemo(
    () => Object.keys(glossaryMap || {}),
    [glossaryMap],
  );

  const contextValue = useMemo(
    () => ({
      openGlossaryTooltip,
      closeGlossaryTooltip,
      getGlossaryTooltipData,
      isLoadingGlossary,
      glossaryTerms,
    }),
    [getGlossaryTooltipData, isLoadingGlossary, glossaryTerms],
  );

  return (
    <GlossaryTooltipContext.Provider value={contextValue}>
      {children}
      {activeGlossaryTooltip && (
        <TooltipV2
          closeTooltip={closeGlossaryTooltip}
          title={activeGlossaryTooltip.tooltipData?.term}
          subtitle={activeGlossaryTooltip.tooltipData?.category}
          description={
            activeGlossaryTooltip.tooltipData?.description
              ? `${activeGlossaryTooltip.tooltipData.description.slice(0, 200)}...`
              : null
          }
          moreLinkFunction={
            activeGlossaryTooltip.tooltipData
              ? () => {
                  closeGlossaryTooltip();
                  if (
                    activeGlossaryTooltip.navigate &&
                    activeGlossaryTooltip.tooltipData?.term
                  ) {
                    activeGlossaryTooltip.navigate(
                      `/glossary/all?term=${activeGlossaryTooltip.tooltipData.term}`,
                    );
                  }
                }
              : null
          }
          analyticsOpenTooltip={() =>
            glossaryOverlayOnLoad(
              activeGlossaryTooltip?.clickAnalytics || {
                tagId: 28.2,
                glossaryOverlay: {
                  container: "Glossary Overlay",
                  openLabel: "Open",
                },
              },
              activeGlossaryTooltip?.tooltipData?.term,
              activeGlossaryTooltip?.pageName,
            )
          }
          analyticsCloseTooltip={() =>
            glossaryOverlayClose(
              activeGlossaryTooltip?.clickAnalytics || {
                tagId: 28.2,
                glossaryOverlay: {
                  container: "Glossary Overlay",
                  closeLabel: "Close",
                },
              },
              activeGlossaryTooltip?.tooltipData?.term,
              activeGlossaryTooltip?.pageName,
            )
          }
          analyticsMoreClick={() =>
            glossaryOverlayCtaClick(
              activeGlossaryTooltip?.clickAnalytics || {
                tagId: 28.2,
                glossaryOverlay: { container: "Glossary Overlay" },
              },
              activeGlossaryTooltip?.tooltipData?.term,
              "Learn More",
              activeGlossaryTooltip?.pageName,
            )
          }
        />
      )}
    </GlossaryTooltipContext.Provider>
  );
};

export const useGlossaryTooltipContext = () => {
  const ctx = useContext(GlossaryTooltipContext);
  if (!ctx) {
    throw new Error(
      "useGlossaryTooltipContext must be used within a GlossaryTooltipProvider",
    );
  }
  return ctx;
};
